import React from "react";
import { useTranslation } from "react-i18next";
import CustomDomainAdd from "./CustomDomainAdd";
import CustomDomainVerification from "./CustomDomainVerification";
import { CustomDomainListData } from "../types";

// NOTE: only step 1 (Enter domain) is wired up for now, per current scope. Steps 2
// (Verify DNS) and 3 (Provisioning) remain stubs and are flagged as follow-up work.
const STEPS = [
    { step: 1, labelKey: "customDomains.stepEnterDomain", subLabelKey: "customDomains.stepEnterDomainSub" },
    { step: 2, labelKey: "customDomains.stepVerifyDns", subLabelKey: "customDomains.stepVerifyDnsSub" },
    // step 3 isn't used for now.
    // { step: 3, labelKey: "customDomains.stepProvisioning", subLabelKey: "customDomains.stepProvisioningSub" },
] as const;

export default function BaseForm({
    goBackToDomainList,
    initialDomain,
}: {
    goBackToDomainList: () => void;
    // When set, the form skips straight to step 2 (DNS verification) for an
    // already-created domain, e.g. when the user clicks "Verify" from the list.
    initialDomain?: CustomDomainListData;
}) {
    const { t } = useTranslation();
    const [step, setStep] = React.useState(initialDomain ? 2 : 1);
    const [createdDomain, setCreatedDomain] = React.useState<CustomDomainListData | null>(
        initialDomain ?? null,
    );

    return (
        <div className="d-flex flex-column gap-4">
            <div className="d-flex flex-column gap-2">
                <button
                    className="btn btn-link p-0 text-decoration-none align-self-start"
                    onClick={goBackToDomainList}
                >
                    &larr; Back to Domains
                </button>
                <h3 className="fs-3 fw-bold text-dark mb-0">{t("customDomains.addCustomDomainTitle")}</h3>
            </div>

            <div className="d-flex align-items-start justify-content-between">
                {STEPS.map(({ step: s, labelKey, subLabelKey }, index) => (
                    <React.Fragment key={s}>
                        <div className="d-flex align-items-start gap-2">
                            <span
                                className={`d-flex align-items-center justify-content-center rounded-circle fw-semibold flex-shrink-0 ${
                                    s === step
                                        ? "bg-primary text-white"
                                        : s < step
                                        ? "bg-success text-white"
                                        : "bg-light text-muted border"
                                }`}
                                style={{ width: 32, height: 32 }}
                            >
                                {s}
                            </span>
                            <div>
                                <div className={`fw-semibold ${s === step ? "text-dark" : "text-muted"}`}>
                                    {t(labelKey)}
                                </div>
                                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                    {t(subLabelKey)}
                                </div>
                            </div>
                        </div>
                        {index < STEPS.length - 1 && (
                            <div className="flex-grow-1 border-top mt-4 mx-3" />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {step === 1 && (
                <CustomDomainAdd
                    onCancel={goBackToDomainList}
                    onSuccess={(domain) => {
                        setCreatedDomain(domain);
                        setStep(2);
                    }}
                />
            )}
            {step === 2 && createdDomain && (
                <CustomDomainVerification
                    domainId={createdDomain.id}
                    hostname={createdDomain.hostname}
                    // onVerified={() => setStep(3)}
                    // just go back to domain list
                    onVerified={goBackToDomainList}
                    onExit={goBackToDomainList}
                />
            )}

            {/* This step may be useful for future, but for now, step 2 is is enough */}
            {/* {step === 3 && (
                <div className="text-muted small fst-italic">
                    {t("customDomains.stepProvisioningPlaceholder")}
                </div>
            )} */}
        </div>
    );
}
